import DeliveryAddressSelect from '@components/dealer-table/DeliveryAddressSelect';
import { Button, CircularProgress, Grid, IconButton, makeStyles } from '@material-ui/core';
import { Cancel, OpenInNew, Save } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { navigate } from '@reach/router';
import { useEffect, useState } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useApi from 'src/hooks/useApi';
import ApiService from 'src/modules/api-service';

import { RoutePaths } from '../constants';
import { DeliveryAddressResponse, TransportOptionsDTO } from '../modules/generated/api';

import { TransportSelectBoughtCar } from './bid-buy/TransportSelect';
import { BoughtCarDeliveryAddressForm } from './BoughtCarDeliveryAddressTransportation';

type DeliveryAddressFormProps = {
  deliveryAddresses: DeliveryAddressResponse[];
  isLoading?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  methods: UseFormReturn<BoughtCarDeliveryAddressForm>;
  showLink?: boolean;
  onlyDelivery?: boolean;
  bidId?: string;
  originTransportationId?: string;
  deliveryAddressEditable?: boolean;
};

const useStyles = makeStyles((theme) => ({
  buttons: {
    marginTop: theme.spacing(2),
  },
}));

const DeliveryAddressForm = ({
  onCancel,
  onSubmit,
  isLoading,
  methods,
  deliveryAddresses = [],
  showLink,
  onlyDelivery = false,
  bidId,
  originTransportationId,
  deliveryAddressEditable = true, // if unsure rather allow selection and let BE decide if possible
}: DeliveryAddressFormProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { fetch, loading: transportOptionsLoading } = useApi<TransportOptionsDTO>();
  const [transportOptions, setTransportOptions] = useState<TransportOptionsDTO>();
  const { watch, setValue } = methods;
  const transportId = watch('transportOptionId');
  const deliveryId = watch('deliveryAddressId');
  const optionsAvailable = transportOptions?.options && transportOptions.options.length > 0;

  // reset Id if preset value is not available anymore
  useEffect(() => {
    if (deliveryId && deliveryId !== 'empty' && !deliveryAddresses.some((address) => address.id === deliveryId)) {
      setValue('deliveryAddressId', 'empty');
    }
  }, [deliveryId, deliveryAddresses, setValue]);
  useEffect(() => {
    if (
      transportId !== undefined &&
      transportOptions?.options?.find((option) => option.id === transportId) === undefined
    ) {
      setValue('transportOptionId', undefined, { shouldValidate: true });
    } else if (transportOptions?.options?.length === 1) {
      setValue('transportOptionId', transportOptions.options[0].id);
    }
  }, [deliveryId, setValue, transportId, transportOptions?.options]);

  useEffect(() => {
    if (!bidId) return;
    if (!deliveryId || deliveryId === 'empty') return;
    fetch(
      ApiService.transportationController.transportationControllerGetTransportOptionsForBid(bidId!, deliveryId || ''),
    ).then((response) => {
      if (response?.data) setTransportOptions(response.data);
    });
  }, [bidId, deliveryId, fetch]);

  return (
    <FormProvider {...methods}>
      <Grid item container alignItems="center">
        <Grid container spacing={1}>
          <Grid item xs={showLink ? 10 : 12}>
            <DeliveryAddressSelect
              fullWidth
              name="deliveryAddressId"
              deliveryAddresses={deliveryAddresses}
              control={methods.control}
              disabled={!deliveryAddressEditable}
            />
          </Grid>
          {showLink && (
            <Grid item xs={2}>
              <IconButton onClick={() => navigate(RoutePaths.deliveryAddress)}>
                <OpenInNew />
              </IconButton>
            </Grid>
          )}
          {!onlyDelivery && (
            <>
              {!transportOptionsLoading && deliveryId && deliveryId !== 'empty' && (
                <Alert severity={optionsAvailable ? 'info' : 'warning'}>
                  {optionsAvailable
                    ? t('configuration.resetTransportOption')
                    : t('configuration.noTransportOptionsAvailable')}
                </Alert>
              )}
              <Grid item xs={10}>
                <TransportSelectBoughtCar
                  options={transportOptions?.options || []}
                  loading={transportOptionsLoading}
                  disabled={!deliveryId || !optionsAvailable}
                  fullWidth
                  preSelectedId={originTransportationId}
                />
              </Grid>
            </>
          )}
        </Grid>
        <Grid container item spacing={1} className={classes.buttons}>
          <Grid item>
            <Button color="secondary" disabled={isLoading} endIcon={<Cancel />} onClick={onCancel} variant="contained">
              {t('common.cancel')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              color="primary"
              disabled={isLoading || !transportId || (!onlyDelivery && !optionsAvailable)}
              endIcon={isLoading ? <CircularProgress size={20} /> : <Save />}
              type="submit"
              variant="contained"
              onClick={onSubmit}
            >
              {t('common.save')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default DeliveryAddressForm;
